import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "./client";
import type { components } from "./schema";

// Úkoly (M5): seznam, převzetí, přeskočení, splnění, kontrola a šablony.

type Schemas = components["schemas"];
export type Task = Schemas["TaskResponse"];
export type TaskPeriodKind = Schemas["TaskPeriodKind"];
export type TaskListScope = Schemas["TaskListScope"];
export type TaskTimeWindow = Schemas["TaskTimeWindow"];
export type TaskKind = Schemas["TaskKind"];
export type TaskRecurrence = Schemas["TaskRecurrence"];
export type TaskTemplate = Schemas["TaskTemplateResponse"];
export type SaveTaskTemplate = Schemas["SaveTaskTemplateRequest"];
export type CreateTask = Schemas["CreateTaskRequest"];

export const timeWindows: TaskTimeWindow[] = ["Morning", "Day", "Evening", "Any"];
export const taskKinds: TaskKind[] = ["Work", "Prep", "Move"];

const tasksKey = ["tasks"] as const;
const reviewKey = ["task-review"] as const;
const templatesKey = ["task-templates"] as const;

export function useTasks(period: TaskPeriodKind, scope: TaskListScope) {
  return useQuery({
    queryKey: [...tasksKey, period, scope],
    queryFn: async () => unwrap(await api.GET("/api/v1/tasks", { params: { query: { period, scope } } })),
  });
}

type TaskAction = "claim" | "unclaim" | "skip" | "complete" | "reopen";

export function useTaskAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: TaskAction }) => {
      const params = { params: { path: { id } } };
      switch (action) {
        case "claim":
          return unwrap(await api.POST("/api/v1/tasks/{id}/claim", params));
        case "unclaim":
          return unwrap(await api.POST("/api/v1/tasks/{id}/unclaim", params));
        case "skip":
          return unwrap(await api.POST("/api/v1/tasks/{id}/skip", params));
        case "complete":
          return unwrap(await api.POST("/api/v1/tasks/{id}/complete", params));
        case "reopen":
          return unwrap(await api.POST("/api/v1/tasks/{id}/reopen", params));
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: tasksKey });
      void queryClient.invalidateQueries({ queryKey: reviewKey });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateTask) => unwrap(await api.POST("/api/v1/tasks", { body })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tasksKey });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useReviewQueue() {
  return useQuery({ queryKey: reviewKey, queryFn: async () => unwrap(await api.GET("/api/v1/task-review", { params: { query: { days: 7 } } })) });
}

export function useReviewTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, verdict, note }: { id: string; verdict: "Verified" | "Rejected"; note: string | null }) =>
      unwrap(await api.POST("/api/v1/task-review/{id}", { params: { path: { id } }, body: { verdict, note } })),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKey });
      void queryClient.invalidateQueries({ queryKey: tasksKey });
    },
  });
}

export function useBulkVerify() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => unwrap(await api.POST("/api/v1/task-review/bulk", { body: { ids } })),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKey });
      void queryClient.invalidateQueries({ queryKey: tasksKey });
    },
  });
}

export function useTaskTemplates() {
  return useQuery({ queryKey: templatesKey, queryFn: async () => unwrap(await api.GET("/api/v1/task-templates")) });
}

export function useSaveTaskTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: SaveTaskTemplate & { id?: string }) =>
      unwrap(
        id
          ? await api.PUT("/api/v1/task-templates/{id}", { params: { path: { id } }, body })
          : await api.POST("/api/v1/task-templates", { body }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: templatesKey });
      void queryClient.invalidateQueries({ queryKey: tasksKey });
    },
  });
}

export function useSetTaskTemplateArchived() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) =>
      unwrap(
        archived
          ? await api.POST("/api/v1/task-templates/{id}/archive", { params: { path: { id } } })
          : await api.POST("/api/v1/task-templates/{id}/restore", { params: { path: { id } } }),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: templatesKey }),
  });
}
